import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
	clearCourse,
	isAdminRole,
	nullAccessibility,
	setAccessibility,
	setAdminSessionSynced,
	setDistrict,
	setIsLoggedIn,
	setSchool,
	setUser,
	UserRole,
} from "../../app/userSlice";
import { isString } from "../../app/utils";
import { applyAccessibility } from "../accessibility/accessibilityUtil";
import { useGetSelfMutation } from "../users/usersApiSlice";

// Shared "finish signing in" bootstrap: refresh the current user and route to the
// appropriate landing page. Used by the login form, the legacy-login handoff, and
// the impersonation handoff so they all resolve identity the same way (and land
// directly on the destination rather than flashing the login screen).
//
// Pushing the school/district into the server-side session is NOT done here: it
// has to wait until the terms of service and any forced password change are done,
// so Private owns it (see useSyncAdminSession).
export function useCompleteSignIn() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { userId: loggedInUserId } = useAppSelector((state) => state.user);

	const [callGetSelf] = useGetSelfMutation();

	return useCallback(
		(options?: { from?: string }) => {
			const from = options?.from;

			return callGetSelf()
				.unwrap()
				.then((payload) => {
					dispatch(setIsLoggedIn(true));
					// A new session has begun, so whatever school/district the previous one
					// pushed into the server session no longer counts. This matters for the
					// impersonation handoff, which resets the RTK Query cache but not the
					// user slice - without it the latch would survive an identity swap as
					// stale-true and Private would skip syncing the new identity.
					dispatch(setAdminSessionSynced(false));
					dispatch(setUser({ ...payload }));
					dispatch(
						setAccessibility(
							payload.accessibility
								? { ...payload.accessibility }
								: { ...nullAccessibility }
						)
					);
					dispatch(setSchool(payload.school));
					dispatch(setDistrict(payload.district));
					applyAccessibility(payload.accessibility);

					// Only clear the course when the identity actually changed - a teacher
					// signing back in as themselves would otherwise lose their current
					// course and be bounced to /courses by RequireCourse.
					if (payload.userId !== loggedInUserId) {
						dispatch(clearCourse());
					}

					if (!payload.bypassTermsOfService && !payload.isImpersonating) {
						navigate("/termsOfService");
					} else if (payload.requiresAccountConfirmation && !payload.isImpersonating) {
						navigate("/myAccount");
					} else if (from) {
						navigate(from);
					} else if (
						payload.role == UserRole.Writer ||
						payload.role == UserRole.Proctor
					) {
						navigate("/courses");
					} else if (payload.role == UserRole.SchoolAdmin && !payload.school) {
						navigate("/selectSchool");
					} else if (payload.role == UserRole.DistrictAdmin && !payload.district) {
						navigate("/selectDistrict");
					} else {
						isAdminRole(payload.role) ? navigate("/adminHome") : navigate("/home");
					}
				})
				.catch((error: any) => {
					const e = isString(error) ? error : JSON.stringify(error);
					console.log(`error calling GetSelf, error = ${e}`);
				});
		},
		[callGetSelf, dispatch, loggedInUserId, navigate]
	);
}
